
import { inject, Injectable, signal, DOCUMENT } from '@angular/core';
import { APP_WINDOW } from '@bhindle/shared/core';
import { ThemeType } from '../entities';

@Injectable()
export class ThemeService {
  #document = inject(DOCUMENT);
  #window: Window = inject(APP_WINDOW);

  constructor() {
    this.#init();
  }

  public setAppearance(theme: 'light' | 'dark' | 'auto') {
    localStorage.setItem('theme', theme);
    this.#updateAppearance();
  }

  public getAppearance(): 'light' | 'dark' | 'auto' {
    return (
      (localStorage.getItem('theme') as 'light' | 'dark' | 'auto') || 'auto'
    );
  }

  /** Stored preference ('auto' included) — reactive counterpart of getAppearance(). */
  public readonly appearance = signal<'light' | 'dark' | 'auto'>('auto');

  public theme = signal<ThemeType>('default');

  /** Resolved appearance ('auto' already applied) — reactive counterpart of [data-theme]. */
  public readonly resolvedAppearance = signal<'light' | 'dark'>('light');

  public setTheme(theme: ThemeType) {
    this.theme.set(theme);
  }

  public getTheme(): ThemeType {
    return (localStorage.getItem('theme-name') as ThemeType) ?? 'default';
  }

  #init() {
    this.#updateAppearance();

    this.#window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        this.#updateAppearance();
      });
  }

  #updateAppearance() {
    const stored = localStorage.getItem('theme');
    // Anything but an explicit light/dark (junk, legacy values) means "auto".
    const theme = stored === 'light' || stored === 'dark' ? stored : 'auto';

    const resolved =
      theme === 'auto'
        ? this.#window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme;
    this.#document.documentElement.dataset['theme'] = resolved;
    this.resolvedAppearance.set(resolved);
    this.appearance.set(theme);
  }
}
